import Checkbox from '@atlaskit/checkbox';
import DynamicTable from '@atlaskit/dynamic-table';
import { RowType } from '@atlaskit/dynamic-table/types';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useImportMappings } from '../../../../hooks/useImportMappings';
import { useDatabaseContext } from '../../../../screens/DatabaseContextProvider';
import { TogglTimeEntry } from '../../../../types/toggl-api';
import { CheckboxCell } from '../cells/CheckboxCell';
import { DescriptionCell } from '../cells/DescriptionCell';
import { DurationCell } from '../cells/DurationCell';
import { IssueKeyCell } from '../cells/IssueKeyCell';
import { StatusCell } from '../cells/StatusCell';
import { SyncButtonCell } from '../cells/SyncButtonCell';
import { useSelectedTimeEntries } from '../hooks/useSelectedTimeEntries';
import { useTableIssueKeys } from '../hooks/useTableIssueKeys';
import { getDisablementReason, getFormattedDate, getTimestamp } from '../utils';
import { NoResults } from './NoResults';
import { SyncAllButton } from './SyncAllButton';

const PAGE_SIZE = 10;

const generateHeader = (
  someSelected: boolean,
  allSelected: boolean,
  togglAllSelected: (value: boolean) => void
) => ({
  cells: [
    {
      key: 'id',
      content: (
        <Checkbox
          isChecked={someSelected}
          isIndeterminate={someSelected && !allSelected}
          onChange={(e) => togglAllSelected(e.target.checked)}
        />
      ),
      width: 1,
    },
    { key: 'name', content: 'Entry name', isSortable: true },
    { key: 'start', content: 'Date', isSortable: true, width: 20 },
    {
      key: 'duration',
      content: <span id="th-duration">Duration</span>,
      isSortable: true,
      width: 12,
    },
    { key: 'issue', content: 'Issue', width: 12 },
    { key: 'status', content: 'Status', width: 5, isSortable: true },
    { key: 'action', content: '', width: 1 },
  ],
});

interface TimeEntriesTableProps {
  isLoading: boolean;
  timeEntries: TogglTimeEntry[];
  selected: number[];
  setSelected: Dispatch<SetStateAction<number[]>>;
}

export function TimeEntriesTable({
  timeEntries,
  selected,
  setSelected,
  isLoading,
}: TimeEntriesTableProps) {
  const [page, setPage] = useState<number>(1);
  const { importMappings } = useImportMappings();
  const {
    data: { personalConfiguration },
  } = useDatabaseContext();

  const { issueKeys, setIssueKey, detectedIssueKeys } = useTableIssueKeys(
    importMappings,
    timeEntries
  );

  const filteredTimeEntries = useMemo(
    () =>
      personalConfiguration.hideTimeEntryWithoutIssueKey
        ? timeEntries.filter((entry) => detectedIssueKeys[entry.id])
        : timeEntries,
    [
      timeEntries,
      personalConfiguration.hideTimeEntryWithoutIssueKey,
      detectedIssueKeys,
    ]
  );

  const {
    allSelected,
    someSelected,
    isSelected,
    isChecked,
    isRowSelectionDisabled,
    togglOneSelected,
    togglAllSelected,
    syncSelected,
    isSyncLoading,
    syncProgress,
  } = useSelectedTimeEntries(
    selected,
    setSelected,
    filteredTimeEntries,
    importMappings,
    issueKeys
  );

  const timeEntryRows: RowType[] = useMemo(
    () =>
      filteredTimeEntries.map((timeEntry) => {
        const timeEntryMapping = importMappings[timeEntry.id];

        const disablementReason = getDisablementReason(
          timeEntry.duration < 60,
          Boolean(issueKeys[timeEntry.id])
        );

        return {
          key: `${timeEntry.id}`,
          onClick: togglOneSelected(timeEntry, timeEntryMapping),
          cells: [
            {
              key: timeEntry.id,
              content: (
                <CheckboxCell
                  isChecked={isChecked(timeEntry.id, timeEntryMapping)}
                  isDisabled={isRowSelectionDisabled(
                    timeEntry,
                    timeEntryMapping
                  )}
                  tooltip={timeEntryMapping ? '' : disablementReason}
                />
              ),
            },
            {
              key: timeEntry.description,
              content: <DescriptionCell description={timeEntry.description} />,
            },
            {
              key: getTimestamp(timeEntry.start),
              content: getFormattedDate(timeEntry.start),
            },
            {
              key: timeEntry.duration,
              content: <DurationCell duration={timeEntry.duration} />,
            },
            {
              key: timeEntryMapping?.jiraIssueKey || 'not-found',
              content: (
                <IssueKeyCell
                  isDisabled={timeEntry.duration < 60}
                  mappedJiraIssueKey={timeEntryMapping?.jiraIssueKey}
                  tempJiraIssueKey={issueKeys[timeEntry.id]}
                  timeEntry={timeEntry}
                  onChange={setIssueKey}
                />
              ),
            },
            {
              key: timeEntryMapping ? 'synced' : 'unsynced',
              content: (
                <StatusCell
                  mapping={timeEntryMapping}
                  timeEntry={timeEntry}
                  isSynced={Boolean(timeEntryMapping)}
                />
              ),
            },
            {
              key: timeEntry.id,
              content: (
                <SyncButtonCell
                  issueKey={issueKeys[timeEntry.id]}
                  isSynced={Boolean(timeEntryMapping)}
                  mapping={timeEntryMapping}
                  timeEntry={timeEntry}
                  tooltip={
                    timeEntryMapping ? 'Unsync' : disablementReason || 'Sync'
                  }
                  setSelected={setSelected}
                />
              ),
            },
          ],
        };
      }),
    [filteredTimeEntries, importMappings, isSelected, issueKeys]
  );

  // align duration header to the right
  useEffect(() => {
    const header =
      document.querySelector('#th-duration')?.parentElement?.parentElement;
    if (header) {
      header.style.textAlign = 'right';
      header.style.paddingRight = '24px';
    }
  }, []);

  return (
    <TableStyles>
      <SyncAllButton
        selectedAmount={selected.length}
        syncSelected={syncSelected}
        isSyncLoading={isSyncLoading}
        syncProgress={syncProgress}
      />

      <DynamicTable
        head={generateHeader(someSelected, allSelected, togglAllSelected)}
        rows={timeEntryRows}
        page={page}
        rowsPerPage={PAGE_SIZE}
        onSetPage={(page) => setPage(page)}
        defaultSortKey="start"
        defaultSortOrder="DESC"
        emptyView={<NoResults />}
        isLoading={isLoading}
      />
    </TableStyles>
  );
}

const TableStyles = styled.div`
  position: relative;
  thead {
    tr {
      height: 24px;
    }
  }
  th {
    vertical-align: middle !important;
  }
  tr {
    height: 44px;
  }
`;
